/**
 * Auth Helper Functions
 * Collects user preferences from browser storage for auth requests.
 *
 * SECURITY: All user inputs are strictly validated before being sent to the backend.
 * - language: Only 'en' or 'ko' (whitelist)
 * - theme: Only 'light', 'dark', or 'system' (whitelist)
 * - timezone: Valid IANA timezone from browser API (validated by Intl)
 */

import { detectTimezone, isValidTimezone } from '@/utils/dateTime'

export interface UserPreferences {
  platform: 'web' | 'ios' | 'android'
  language: 'en' | 'ko'
  theme: 'light' | 'dark' | 'system'
  timezone: string
}

// Allowed values (whitelist approach for security)
const ALLOWED_LANGUAGES = ['en', 'ko'] as const
const ALLOWED_THEMES = ['light', 'dark', 'system'] as const
const DEFAULT_TIMEZONE = 'America/New_York'

/** Parse theme from localStorage, defaulting to 'light' */
function parseTheme(themeRaw: string | null): 'light' | 'dark' | 'system' {
  if (
    themeRaw &&
    ALLOWED_THEMES.includes(themeRaw as (typeof ALLOWED_THEMES)[number])
  ) {
    return themeRaw as 'light' | 'dark' | 'system'
  }
  return 'light'
}

/** Parse language from cookie, defaulting to 'en' */
function parseLanguage(localeRaw: string | null): 'en' | 'ko' {
  if (
    localeRaw &&
    ALLOWED_LANGUAGES.includes(localeRaw as (typeof ALLOWED_LANGUAGES)[number])
  ) {
    return localeRaw as 'en' | 'ko'
  }
  return 'en'
}

/**
 * Collect user preferences from browser storage and cookies.
 * Used by OTP verification and Google OAuth login.
 *
 * All values are validated against whitelists to prevent injection attacks.
 */
export function collectUserPreferences(): UserPreferences {
  // Language from NEXT_LOCALE cookie (validated against whitelist)
  const localeRaw =
    typeof document !== 'undefined'
      ? document.cookie
          .split('; ')
          .find((row) => row.startsWith('NEXT_LOCALE='))
          ?.split('=')[1] || null
      : null
  const language = parseLanguage(localeRaw)

  // Theme from localStorage (validated against whitelist)
  const themeRaw =
    typeof window !== 'undefined'
      ? localStorage.getItem('dday-user-theme')
      : null
  const theme = parseTheme(themeRaw)

  // Timezone from browser API (validated as valid IANA timezone)
  const detectedTimezone = detectTimezone()
  const timezone = isValidTimezone(detectedTimezone)
    ? detectedTimezone
    : DEFAULT_TIMEZONE

  return { platform: 'web', language, theme, timezone }
}
